import React, { FC, useMemo } from 'react'

import { Box, Stack, SxProps, Typography, useTheme } from '@mui/material'
import { CircularProgressbar } from 'react-circular-progressbar'
import { useTranslation } from 'react-i18next'

import { MoneyValue } from '..'
import { calculateRiskRewardRatio } from '../../../utils/riskReward'
import { WidgetWrapper } from '../widgetsComponents'

type RiskRewardWidgetProps = {
  potentialProfit: number
  potentialLoss: number
  currency?: string
  handleCheck?: () => void
  isChecked?: boolean
  sx?: SxProps
}

export const RiskRewardRatioWidget: FC<RiskRewardWidgetProps> = ({
  potentialProfit,
  potentialLoss,
  currency,
  handleCheck,
  isChecked,
  sx,
}) => {
  const { t } = useTranslation()

  const { palette, spacing } = useTheme()

  const { riskRewardRatio, riskPercent } = useMemo(() => {
    if (
      potentialProfit == null ||
      potentialLoss == null ||
      potentialProfit <= 0 ||
      potentialLoss <= 0
    ) {
      return { ristRewardRation: null, riskPercent: 0 }
    }

    const riskRewardRatio = calculateRiskRewardRatio(potentialProfit, potentialLoss)
    const riskPercent = (potentialProfit / (potentialProfit + potentialLoss)) * 100

    return { riskRewardRatio, riskPercent }
  }, [potentialProfit, potentialLoss])

  return (
    <WidgetWrapper
      label={t('riskRewardRatio', 'Risk-reward ratio')}
      handleCheck={handleCheck}
      isChecked={isChecked}
      explanation={t('assessesThePotentialProfitRewardInRelationPotentialLssRiskOfTrade')}
      sx={sx}
    >
      <Stack display="flex" alignItems="center" height="100%" direction={'row'} spacing={5}>
        <Box width={spacing(15.5)} height={spacing(15.5)}>
          <CircularProgressbar
            value={isNaN(riskPercent) ? 0 : riskPercent}
            text={riskRewardRatio?.toFixed(2) || '-'}
            styles={{
              path: {
                stroke: palette.green[400],
                strokeLinecap: 'butt',
                transition: 'stroke-dashoffset 0.5s ease 0s',
                transformOrigin: 'center center',
              },
              trail: {
                stroke: isNaN(riskPercent) ? palette.grey[500] : palette.red[400],
                strokeLinecap: 'butt',
                transformOrigin: 'center center',
              },
              text: {
                fill: palette.text.primary,
                fontSize: spacing(3),
                fontWeight: 'bold',
                fontFamily: 'Wix Madefor Display',
                textAnchor: 'middle',
                transform: 'translate(0, 6px)',
              },
              background: {
                fill: palette.cyan[500],
              },
            }}
            strokeWidth={6}
          />
        </Box>
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          gap={spacing(1.25)}
          justifyContent="center"
          alignItems="flex-start"
        >
          <Box>
            <Typography fontWeight="bold" color={palette.grey[700]} fontSize={12}>
              {t('potentialProfit', 'Potential profit')}
            </Typography>
            <MoneyValue money={potentialProfit} currency={currency} />
          </Box>
          <Box>
            <Typography fontWeight="bold" color={palette.grey[700]} fontSize={12}>
              {t('potentialLoss', 'Potential loss')}
            </Typography>
            <MoneyValue money={potentialLoss} currency={currency} />
          </Box>
        </Box>
      </Stack>
    </WidgetWrapper>
  )
}
